import DoorImage from '../components/DoorImage';
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, ShoppingBag, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { useLang, localized } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { useSettings, whatsappUrl } from "../lib/settings";
import { api, FILE_URL } from "../lib/api";

const DEFAULT_DOOR = "https://images.unsplash.com/photo-1659720879327-827462ca3942?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsJTIwaW50ZXJpb3IlMjBkb29yfGVufDB8fHx8MTc4NDE0NzE2Mnww&ixlib=rb-4.1.0&q=85";

export default function DoorDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { isFavorite, toggleFavorite, addToCart, inCart } = useAuth();
  const { settings } = useSettings();
  const [door, setDoor] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/doors/${id}`).then((r) => setDoor(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <Layout>
        <div className="fd-container py-24 text-center" data-testid="door-not-found">
          <p className="text-neutral-500">Not found.</p>
          <Link to="/catalog" className="fd-btn-ghost mt-6 inline-flex">{t("door.back")}</Link>
        </div>
      </Layout>
    );
  }

  if (!door) {
    return (
      <Layout>
        <div className="fd-container py-24 text-center text-neutral-400" data-testid="door-loading">{t("common.loading")}</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="fd-container pt-8 md:pt-12 pb-4">
        <Link to="/catalog" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="door-back">{t("door.back")}</Link>
      </section>

      <section className="fd-container grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 pb-16 md:pb-24" data-testid="door-detail">
        <div className="md:col-span-7">
          <div className="aspect-[3/4] bg-neutral-100 overflow-hidden">
            <DoorImage src={door.image_id ? FILE_URL(door.image_id) : DEFAULT_DOOR} alt={localized(door.name, lang)} fallback={DEFAULT_DOOR} />
          </div>
        </div>
        <div className="md:col-span-5 flex flex-col">
          <p className="fd-label mb-4">{t(`categories.${door.category}`)}</p>
          <h1 className="text-3xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="door-name">
            {localized(door.name, lang)}
          </h1>
          <p className="text-2xl md:text-3xl mt-4 text-neutral-900" data-testid="door-price">{door.price} {t("common.currency")}</p>
          <p className="mt-8 text-neutral-600 leading-relaxed" data-testid="door-description">
            {localized(door.description, lang)}
          </p>

          <dl className="mt-10 border-t border-neutral-200">
            {door.material && (
              <div className="py-4 border-b border-neutral-200 grid grid-cols-3 gap-4">
                <dt className="fd-label">{t("door.material")}</dt>
                <dd className="col-span-2 text-neutral-900" data-testid="door-material">{door.material}</dd>
              </div>
            )}
            {door.dimensions && (
              <div className="py-4 border-b border-neutral-200 grid grid-cols-3 gap-4">
                <dt className="fd-label">{t("door.dimensions")}</dt>
                <dd className="col-span-2 text-neutral-900" data-testid="door-dimensions">{door.dimensions}</dd>
              </div>
            )}
            {door.colors && door.colors.length > 0 && (
              <div className="py-4 border-b border-neutral-200 grid grid-cols-3 gap-4">
                <dt className="fd-label">{t("door.colors")}</dt>
                <dd className="col-span-2 flex flex-wrap gap-2" data-testid="door-colors">
                  {door.colors.map((c, i) => (
                    <span key={i} className="text-xs px-2.5 py-1 border border-neutral-200 text-neutral-700">{c}</span>
                  ))}
                </dd>
              </div>
            )}
          </dl>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-stretch" data-testid="door-actions">
            <button
              onClick={() => { addToCart(door.id); toast.success(t("cart.added")); }}
              className={`inline-flex items-center justify-center gap-2 px-6 py-3 text-sm border transition-colors ${
                inCart(door.id) ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white"
              }`}
              data-testid="door-add-cart"
            >
              {inCart(door.id) ? <><Check size={16} /> {t("cart.in_cart")}</> : <><ShoppingBag size={16} /> {t("cart.add")}</>}
            </button>
            <button
              onClick={() => toggleFavorite(door.id)}
              className={`inline-flex items-center justify-center gap-2 px-4 py-3 text-sm border transition-colors ${
                isFavorite(door.id) ? "border-red-600 text-red-600" : "border-neutral-300 text-neutral-700 hover:border-neutral-900"
              }`}
              data-testid="door-fav-toggle"
              aria-label="favorite"
            >
              <Heart size={16} fill={isFavorite(door.id) ? "currentColor" : "none"} />
              <span className="hidden sm:inline">{isFavorite(door.id) ? t("auth.favorites") : t("auth.favorites")}</span>
            </button>
            <a
              href={whatsappUrl(settings?.whatsapp, `${t("cart.wa_greeting")}\n\n${localized(door.name, lang)} — ${door.price} ${t("common.currency")}`)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm text-white bg-[#25D366] hover:bg-[#1ebe57] transition-colors"
              data-testid="door-whatsapp"
            >
              <MessageCircle size={16} /> {t("cart.order_wa")}
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
